import { useEffect, useState } from "react";
import { Check } from "lucide-react";

import { useStore } from "../context/StoreContext";
import { GhostBtn, PrimaryBtn, Sheet } from "./ui";

function CheckRow({ selected, onToggle, label, description }) {
  return (
    <button
      onClick={onToggle}
      className={`w-full text-left rounded-xl border p-3 transition flex items-start gap-3 ${
        selected ? "border-foreground/40 bg-foreground/[0.04]" : "border-border hover:bg-foreground/[0.02]"
      }`}
    >
      <span
        className={`mt-0.5 w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${
          selected ? "bg-accent border-accent text-accent-foreground" : "border-border"
        }`}
      >
        {selected ? <Check size={13} /> : null}
      </span>
      <span className="min-w-0">
        <span className="block text-[14px] font-medium text-foreground">{label || "Untitled"}</span>
        {description ? <span className="block text-[12.5px] text-muted mt-0.5 leading-snug">{description}</span> : null}
      </span>
    </button>
  );
}

export default function GenerateModal() {
  const { genModal, setGenModal, settings, generate } = useStore();
  const audiences = settings.audiences || [];
  const themes = settings.themes || [];
  const [selA, setSelA] = useState([]);
  const [selT, setSelT] = useState([]);

  useEffect(() => {
    if (genModal) {
      setSelA([]);
      setSelT([]);
    }
  }, [genModal]);

  const toggle = (arr, setArr, idx) =>
    setArr(arr.includes(idx) ? arr.filter((x) => x !== idx) : [...arr, idx]);

  const run = () => {
    generate({
      audiences: selA.map((i) => audiences[i]).filter(Boolean),
      themes: selT.map((i) => themes[i]).filter(Boolean),
    });
  };

  const nothingSet = audiences.length === 0 && themes.length === 0;

  return (
    <Sheet
      open={genModal}
      onClose={() => setGenModal(false)}
      title="Generate content"
      footer={
        <PrimaryBtn onClick={run} full>
          {selA.length || selT.length ? "Generate focused batch" : "Generate"}
        </PrimaryBtn>
      }
    >
      {nothingSet ? (
        <p className="text-[13.5px] text-muted leading-relaxed">
          You have not set any audiences or themes yet, so this will generate across your whole knowledge base. To
          focus a campaign, add target audiences in Settings and generate themes from your knowledge base on the
          Documents page, then come back here.
        </p>
      ) : (
        <div className="space-y-5">
          <p className="text-[13.5px] text-muted leading-relaxed">
            Choose who this batch is for and which themes to centre it on. Leave a section untouched to include
            everything in it.
          </p>
          {audiences.length > 0 ? (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-faint mb-2">Audience</div>
              <div className="space-y-2">
                {audiences.map((a, i) => (
                  <CheckRow
                    key={i}
                    selected={selA.includes(i)}
                    onToggle={() => toggle(selA, setSelA, i)}
                    label={a.label}
                    description={a.description}
                  />
                ))}
              </div>
            </div>
          ) : null}
          {themes.length > 0 ? (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-faint">Themes</div>
                <button
                  onClick={() => setSelT(selT.length === themes.length ? [] : themes.map((_, i) => i))}
                  className="text-[12px] text-muted hover:text-foreground transition"
                >
                  {selT.length === themes.length ? "Clear" : "Select all"}
                </button>
              </div>
              <div className="space-y-2">
                {themes.map((t, i) => (
                  <CheckRow
                    key={i}
                    selected={selT.includes(i)}
                    onToggle={() => toggle(selT, setSelT, i)}
                    label={t.label}
                    description={t.description}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </Sheet>
  );
}